import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { activatePendingUpdate } from "@/lib/pwa/register";

/**
 * Subtle "New version available" banner. Renders only after the SW registration
 * wrapper dispatches `kaf:sw-update-ready`, which itself only fires in a real
 * production build. Reload happens via `controllerchange` after SKIP_WAITING.
 */
export function ServiceWorkerUpdatePrompt() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onReady = () => setReady(true);
    window.addEventListener("kaf:sw-update-ready", onReady);
    return () => window.removeEventListener("kaf:sw-update-ready", onReady);
  }, []);

  if (!ready) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 bottom-24 z-40 mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-gold/40 bg-obsidian/95 p-3 shadow-halo backdrop-blur sm:inset-x-auto sm:right-6 sm:bottom-6"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
        <RefreshCw size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ivory">New version available</p>
        <p className="text-[11px] text-muted-foreground">Reload to get the latest temple.</p>
      </div>
      <button
        type="button"
        onClick={() => activatePendingUpdate()}
        className="rounded-full bg-gold-gradient px-3 py-1.5 text-xs font-semibold text-obsidian"
      >
        Reload
      </button>
      <button
        type="button"
        onClick={() => setReady(false)}
        aria-label="Dismiss update notice"
        className="rounded-full p-1 text-muted-foreground hover:text-gold"
      >
        <X size={14} />
      </button>
    </div>
  );
}
